import { LayoutServicePageState, useSitecoreContext, TextField, Link, Item } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { dataFetcher } from 'lib/data-fetcher';
import { getQueryStringParameter, getCleanID, getLanguageBasedUrl } from 'lib/utils';

import type { TableCellData, TableRowData } from 'components/helpers/Table/Table.types';
import Table, { TableProps } from 'components/helpers/Table/Table';
import Modal from 'components/helpers/Modal';
import Tooltip from 'components/helpers/Tooltip/Tooltip';
import { buildURL, buildApplyToEnrollURL, checkEnrollEnable } from 'components/PlanData/utilities';

import { useDictionary } from 'lib/utils';
import { DrugCoverageTableGroup } from '../Types/DrugCoverageTables.Type';
import { PlanYear } from '../Types/PlanYear.Type';
import { PlanDetailGroup } from '../Types/PlanDetailGroup.Type';
import { PlanResponse } from '../Types/GetPlanDetailsResponse.Type';
import { PlanType } from './PlanComparisonTable.Types';
import Icon from 'components/helpers/Icon';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';

enum LoadingState {
  start = 'Starting',
  loadingPlans = 'Loading plans',
  plansLoaded = 'Plans loaded',
  loadTopRows = 'Loading top rows',
  topRowsLoaded = 'Top rows loaded',
  loadTables = 'Loading tables',
  end = 'Finished',
}

export type PlanComparisonTableFields = {
  planType: PlanType;
  crmPrefix: string;
  planDetailGroupLayout: Array<PlanDetailGroup>;
  rowNotCoveredText: TextField;
  rxTableNotCoveredText: TextField;
  rxRowNotCoveredText: TextField;
  showRxDrugCoverageTable: {
    value: boolean;
  };
  drugCoverageGroups: Array<DrugCoverageTableGroup>;
  planYears: PlanYear[];
  useCavulus: boolean;
};

export type PlanClassNameResolver = (plan: PlanResponse) => string;

type ClassNameValue = string | number | boolean | null | undefined | { [key: string]: unknown } | ClassNameValue[];
type ClassNameResolver = (...classes: ClassNameValue[]) => string;

export type UsePlanComparisonTableOptions = {
  fields: PlanComparisonTableFields;
  cx: ClassNameResolver;
  planSearchPage: Item;
  getPlanClassName?: PlanClassNameResolver; // SAR overrides this, base doesn't
  isEnrollAllowedForPlan?: (plan: PlanResponse) => boolean;
};

export function usePlanComparisonTable(options: UsePlanComparisonTableOptions) {
  const { fields, cx, planSearchPage, getPlanClassName: resolvePlanClassName, isEnrollAllowedForPlan } = options;
  const router = useRouter();
  const { sitecoreContext } = useSitecoreContext();
  const viewPlanDetailsLabel = useDictionary('View Plan Details');
  const viewApplyToEnrollLabel = useDictionary('Apply to Enroll');

  const {
    planType,
    planDetailGroupLayout,
    drugCoverageGroups,
    showRxDrugCoverageTable,
    rxTableNotCoveredText,
    rxRowNotCoveredText,
    rowNotCoveredText,
    planYears,
    useCavulus,
    crmPrefix,
  } = fields;

  const planDetailPage = planType?.fields?.planDetailPage;

  const [stage, setStage] = useState<LoadingState>(LoadingState.start);
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [tables, setTables] = useState<TableProps[]>([]);
  const [topRow, setTopRow] = useState<TableCellData[]>([{ children: '', className: cx('placeholder') }]);
  const [topRowNoLink, setTopRowNoLink] = useState<TableCellData[]>([{ children: '', className: cx('placeholder') }]);
  const [applyRow, setApplyRow] = useState<TableCellData[]>([{ children: '', className: cx('placeholder') }]);
  const [showNoResultsFound, setShowNoResultsFound] = useState<boolean>(false);
  const baseUrl = process.env['PUBLIC_URL'] || '';
  const county = getQueryStringParameter('county', router.query);
  const year = getQueryStringParameter('year', router.query);
  const zipcode = getQueryStringParameter('zipcode', router.query);
  const existingMember = getQueryStringParameter('ExistingMember', router.query);
  const state = getQueryStringParameter('state', router.query);
  const [enableEnroll, setEnableEnroll] = useState<boolean>();
  const { pageEditing } = sitecoreContext;

  useEffect(() => {
    async function loadPlans() {
      const regex = /^\d{4}-\d{3}(?:-\d{3})?$/;
      const isSarPlan = router.query.plan0 && regex.test(router.query.plan0 as string);

      // if sar URL let parent component resolve valid plan IDs first
      if (isSarPlan) {
        return;
      }

      setStage(LoadingState.loadingPlans);

      const plansInQueryString = Object.keys(router.query).filter((x) => x.startsWith('plan')).length;
      if (!pageEditing && (plansInQueryString === 0 || !county || !year || !zipcode || !existingMember)) {
        router.push(getLanguageBasedUrl(planSearchPage?.url ?? '', sitecoreContext.language));
        return;
      }
      const planIds: string[] = [];
      let planIndex = 0;
      let planId = getQueryStringParameter('plan0', router.query);

      // Get plan IDs in plan0, plan1, plan2... order
      while (planId !== '') {
        planIds.push(planId);
        planIndex++;
        planId = getQueryStringParameter(`plan${planIndex}`, router.query);
      }

      const loadedPlans = await Promise.all(
        planIds.map(async (planId) => {
          try {
            const response = await dataFetcher<PlanResponse[]>(`${baseUrl}/api/getPlanDetails`, {
              planTypeId: planType.id,
              planItemId: planId,
              includeDocFields: false,
              includeBenefitsFields: false,
              language: sitecoreContext.language,
            });

            const $data = response.data.filter((d) => d.planName.value);

            if ($data.length > 0 && $data[0].id) {
              return $data[0];
            }

            return {
              id: planId,
              planName: { value: 'Plan failed to load' },
              planDescription: { value: '' },
              contract: { value: '' },
              pbp: { value: '' },
              groupId: { value: '' },
              mcproductid: { value: '' },
              starRating: { targetItem: { value: { value: '' } } },
              additionalBenefits: {
                targetItem: {
                  caption: { value: '' },
                  value: { value: '' },
                },
              },
              documents: { targetItems: [] },
              planDetailEntries: { targetItems: [] },
              prescriptionDrugTables: { targetId: '' },
              planYear: {
                targetItem: {
                  year: {
                    value: '',
                  },
                },
              },
            };
          } catch {
            setShowNoResultsFound(true);

            return {
              id: planId,
              planName: { value: 'Plan failed to load' },
              planDescription: { value: '' },
              contract: { value: '' },
              pbp: { value: '' },
              groupId: { value: '' },
              mcproductid: { value: '' },
              starRating: { targetItem: { value: { value: '' } } },
              additionalBenefits: {
                targetItem: {
                  caption: { value: '' },
                  value: { value: '' },
                },
              },
              documents: { targetItems: [] },
              planDetailEntries: { targetItems: [] },
              prescriptionDrugTables: { targetId: '' },
              planYear: {
                targetItem: {
                  year: {
                    value: '',
                  },
                },
              },
            };
          }
        })
      );

      setPlans(loadedPlans);
      setStage(LoadingState.plansLoaded);
    }

    function loadTopRows() {
      setStage(LoadingState.loadTopRows);

      plans.map((thisPlan) => {
        const tempQueries: [string, string][] = [
          ['planId', thisPlan.id],
          ['county', county],
          ['year', year],
          ['zipcode', zipcode],
          ['state', state],
          ['ExistingMember', existingMember],
        ];
        let tempRow = topRow;
        const planClass = resolvePlanClassName?.(thisPlan) ?? '';
        const canEnroll = isEnrollAllowedForPlan?.(thisPlan) ?? true;
        const enrollEnabled = checkEnrollEnable(planYears, parseInt(year));
        setEnableEnroll(enrollEnabled);

        tempRow.push({
          children: (
            <>
              {enrollEnabled ? (
                isEnrollAllowedForPlan && canEnroll ? (
                  <Link
                    id="enroll"
                    className={cx('applyToEnrollLink')}
                    field={{ value: viewApplyToEnrollLabel }}
                    onClick={() => {
                      handleApplyToEnroll(thisPlan);
                    }}
                    href="#enroll"
                  >
                    {viewApplyToEnrollLabel}
                    <Icon variant="carat" />
                  </Link>
                ) : (
                  <span className={cx('enroll-spacer')} aria-hidden="true" />
                )
              ) : null}
              <RichTextWrapper field={{ value: thisPlan.planName.value?.toString() }} editable={false} />
              <a href={buildURL(planDetailPage?.url || '', null, tempQueries, sitecoreContext.language)}>
                {viewPlanDetailsLabel}
              </a>
              {isEnrollAllowedForPlan && (
                <div>
                  <span className={cx('plan-year')}>
                    {canEnroll ? thisPlan.planYear?.targetItem?.year?.value : 'Current plan'}
                  </span>
                </div>
              )}
            </>
          ),
          className: cx('planName', 'col' + plans.length, planClass),
          isHeader: true,
        });

        setTopRow(tempRow);
        tempRow = topRowNoLink;
        tempRow.push({
          children: (
            <>
              <RichTextWrapper field={{ value: thisPlan.planName.value?.toString() }} editable={false} />
              {isEnrollAllowedForPlan && (
                <div>
                  <span className={cx('plan-year')}>
                    {canEnroll ? thisPlan.planYear?.targetItem?.year?.value : 'Current plan'}
                  </span>
                </div>
              )}
            </>
          ),
          className: cx('planName', 'col' + plans.length, planClass),
          isHeader: true,
        });
        setTopRowNoLink(tempRow);

        const tempApplyRow = applyRow;
        tempApplyRow.push({
          children: canEnroll ? (
            <>
              <RichTextWrapper field={{ value: thisPlan.planName.value?.toString() }} editable={false} />
              <br />
              <Link
                id="enroll"
                className={cx('applyToEnrollLink')}
                field={{ value: viewApplyToEnrollLabel }}
                onClick={() => {
                  handleApplyToEnroll(thisPlan);
                }}
                href="#enroll"
              >
                {viewApplyToEnrollLabel}
                <Icon variant="carat"></Icon>
              </Link>
            </>
          ) : null,
          className: cx('planName', 'apply', 'col' + plans.length, planClass, !canEnroll && 'no-enroll'),
          isHeader: true,
        });
        setApplyRow(tempApplyRow);
      });
      setStage(LoadingState.topRowsLoaded);
    }

    const handleApplyToEnroll = async (thisPlan: PlanResponse) => {
      window.location.href = await buildApplyToEnrollURL({
        planTypeName: planType.fields.name.value,
        zipcode: zipcode,
        county: county,
        year: year,
        planName: thisPlan.planName.value,
        contract: thisPlan.contract.value,
        pbp: thisPlan.pbp.value,
        groupID: thisPlan.groupId.value,
        crmPrefix: crmPrefix,
        mcProductID: thisPlan.mcproductid.value?.toString(),
        cta: 'applytoenroll',
      });
    };

    if (sitecoreContext.pageState === LayoutServicePageState.Edit) {
      setStage(LoadingState.end);
      const newTable: TableProps = {
        id: 'sampleTable',
        rowData: [
          {
            placement: 'header',
            cellData: [
              { children: '', className: cx('placeholder') },
              { children: 'Plan 1', className: cx('planName', 'col' + plans.length) },
              { children: 'Plan 2', className: cx('planName', 'col' + plans.length) },
              { children: 'Plan 3', className: cx('planName', 'col' + plans.length) },
            ],
          },
          {
            placement: 'header',
            cellData: [
              {
                children: 'Table Name',
                colSpan: 4,
                className: cx('header'),
              },
            ],
          },
          {
            cellData: [
              { children: 'Row name', isHeader: true },
              { children: 'Plan 1 coverage' },
              { children: 'Plan 2 coverage' },
              { children: 'Plan 3 coverage' },
            ],
          },
          {
            cellData: [
              {
                children:
                  'Plan Comparison data is pulled from multiple sources in Global Data > Plan Data and must be changed there',
                colSpan: 4,
                className: cx('no-plan'),
              },
            ],
          },
        ],
        className: cx('plan-comparison-table'),
      };

      setTables([newTable]);
    }

    if (sitecoreContext.pageState === LayoutServicePageState.Normal) {
      if (router.isReady && stage === LoadingState.start) {
        loadPlans();
      }

      if (stage === LoadingState.plansLoaded) {
        loadTopRows();
      }

      if (stage === LoadingState.topRowsLoaded) {
        setStage(LoadingState.loadTables);
        const tempTables: TableProps[] = [];

        //Detail Tables
        planDetailGroupLayout.map((group) => {
          const newTable: TableProps = {
            id: group.id,
            rowData: [],
            className: cx('plan-comparison-table'),
          };

          //add top row, only first table should have links
          newTable.rowData?.push({
            placement: 'header',
            cellData: tempTables.length === 0 ? topRow : topRowNoLink,
          });

          //add table header with name(caption)
          const captionField = <RichTextWrapper field={{ value: group.caption }} />;
          newTable.rowData?.push({
            placement: 'header',
            cellData: [
              {
                children: captionField,
                colSpan: plans.length + 1,
                className: cx('header'),
              },
            ],
          });

          let addTable = false;
          group.planDetailRows.map((thisRow) => {
            const cells: TableCellData[] = Array<TableCellData>();
            let labelField = <RichTextWrapper field={{ value: thisRow.label }} />;
            if (thisRow.tooltip !== '') {
              labelField = (
                <div className={cx('tooltip-wrapper')}>
                  <RichTextWrapper field={{ value: thisRow.label }} />
                  <Tooltip field={{ value: thisRow.tooltip }} className={cx('compare-tooltip')} direction="custom" />
                </div>
              );
            }

            cells.push({ children: labelField, isHeader: true });

            let showRow = false;

            plans.map((thisPlan) => {
              let cellField = <RichTextWrapper field={{ value: rowNotCoveredText.value?.toString() }} />;
              const rowEntryIDs = thisRow.planEntries.map((entry) => getCleanID(entry.id));
              const planClass = resolvePlanClassName?.(thisPlan) ?? '';
              for (let x = 0; x < thisPlan.planDetailEntries.targetItems.length; x++) {
                if (rowEntryIDs.includes(thisPlan.planDetailEntries.targetItems[x].id)) {
                  showRow = true;
                  cellField = (
                    <RichTextWrapper
                      field={{ value: thisPlan.planDetailEntries.targetItems[x].description.value?.toString() }}
                    />
                  );
                  break;
                }
              }

              cells.push({ children: cellField, className: cx(planClass) });
            });

            if (showRow) {
              newTable.rowData?.push({ cellData: cells });
              addTable = true;
            }
          });

          if (addTable) tempTables.push(newTable);
        });

        if (showRxDrugCoverageTable) {
          drugCoverageGroups.forEach((coverageGroup) => {
            const allOptionIDs = coverageGroup.options.map((drugTable) => getCleanID(drugTable.id));
            const planSelections = plans.map((pln) => pln.prescriptionDrugTables.targetId);

            if (allOptionIDs.filter((id) => planSelections.includes(id)).length > 0) {
              const prescriptionTable: TableProps = {
                id: coverageGroup.id,
                rowData: [],
                className: cx('plan-comparison-table'),
              };

              prescriptionTable.rowData?.push({
                placement: 'header',
                cellData: tempTables.length === 0 ? topRow : topRowNoLink,
              });

              prescriptionTable.rowData?.push({
                placement: 'header',
                cellData: [
                  {
                    children: <RichTextWrapper field={{ value: coverageGroup.planComparisonTableHeader }} />,
                    colSpan: plans.length + 1,
                    className: cx('header'),
                  },
                ],
              });

              const cells: TableCellData[] = Array<TableCellData>();
              cells.push({ children: <></>, isHeader: true });

              for (let x = 0; x < planSelections.length; x++) {
                const selectedOption = coverageGroup.options.find(
                  (drugTable) => getCleanID(drugTable.id) == planSelections[x]
                );
                const planClass = resolvePlanClassName?.(plans[x]) ?? '';
                if (selectedOption != undefined) {
                  const modalTable: TableProps = {
                    id: 'modalTbl' + getCleanID(plans[x].id) + selectedOption.id,
                    rowData: [
                      {
                        placement: 'header',
                        cellData: [
                          {
                            children: <RichTextWrapper field={{ value: coverageGroup.groupCaption }} />,
                            colSpan: plans.length + 1,
                            className: cx('header'),
                          },
                        ],
                      },
                    ],
                    className: cx('plan-comparison-table'),
                  };

                  selectedOption.drugCoverageTables.map((thisRxTable) => {
                    if (thisRxTable.rows.length > 0) {
                      modalTable.rowData?.push({
                        cellData: [
                          {
                            children: <></>,
                            colSpan: thisRxTable.columnGroup.columns.length,
                            className: cx('placeholder'),
                          },
                        ],
                      });
                      modalTable.rowData?.push({
                        cellData: [
                          {
                            children: <RichTextWrapper field={{ value: thisRxTable.caption }} />,
                            isHeader: true,
                            colSpan: thisRxTable.columnGroup.columns.length,
                            className: cx('rx-table-header'),
                          },
                        ],
                      });
                    }

                    thisRxTable.rows.map((newRow) => {
                      const newRowData: TableRowData = { cellData: [] };
                      let firstCell = true;
                      for (let x = 0; x < thisRxTable.columnGroup.columns.length; x++) {
                        //newRow.cells.map((newCell) => {
                        const newCell = newRow.cells[x];
                        let labelField = <></>;
                        let tempClass = cx('rx-table-cell');

                        if (newCell) {
                          const bgColor = thisRxTable.columnGroup.columns[x].color;
                          labelField = (
                            <RichTextWrapper
                              field={{
                                value: newCell.label === '' ? rxRowNotCoveredText.value?.toString() : newCell.label,
                              }}
                            />
                          );

                          if (newRow.tooltip !== '' && firstCell) {
                            labelField = (
                              <div className={cx('tooltip-wrapper')}>
                                <RichTextWrapper
                                  field={{
                                    value: newCell.label === '' ? rxRowNotCoveredText.value?.toString() : newCell.label,
                                  }}
                                />
                                <Tooltip
                                  field={{ value: newRow.tooltip }}
                                  className={cx('compare-tooltip')}
                                  direction="custom"
                                />
                              </div>
                            );
                          }

                          if (newCell.isHeader) {
                            tempClass += ' ' + cx('bold-text');
                          }
                          if (bgColor !== '') {
                            tempClass += x === 0 ? ' bg-gray15' : ' bg-' + bgColor;
                          }
                        }
                        newRowData.cellData.push({
                          children: labelField,
                          isHeader: firstCell,
                          className: cx(tempClass),
                        });
                        firstCell = false;
                      }
                      modalTable.rowData?.push(newRowData);
                    });
                  });

                  const cellField = (
                    <Modal
                      title={plans[x].planName.value + ' Table'}
                      launcherText={'View ' + selectedOption.caption + ' Table'}
                      className={cx('rxModalWindow')}
                      width="100%"
                      tabIndex={0}
                      openWithSpacebar={true}
                      openWithReturn={true}
                    >
                      <Table {...modalTable}></Table>
                    </Modal>
                  );

                  cells.push({ children: cellField, className: cx('col' + plans.length, planClass) });
                } else {
                  const cellField = (
                    <RichTextWrapper field={{ value: rxTableNotCoveredText.value?.toString() }} editable={false} />
                  );

                  cells.push({ children: cellField, className: cx('col' + plans.length, planClass) });
                }
              }
              prescriptionTable.rowData?.push({ cellData: cells });

              tempTables.push(prescriptionTable);
            }
          });
        }

        if (enableEnroll) {
          //table with Apply Here links is always last
          const applyTable: TableProps = {
            id: 'applyTble',
            rowData: [{ placement: 'header', cellData: applyRow }],
            className: cx('plan-comparison-table'),
          };

          tempTables.push(applyTable);
        }

        setTables(tempTables);
        setStage(LoadingState.end);
      }
    }
  }, [
    applyRow,
    baseUrl,
    county,
    drugCoverageGroups,
    enableEnroll,
    existingMember,
    planDetailGroupLayout,
    planDetailPage,
    planSearchPage,
    plans,
    planType,
    planYears,
    rowNotCoveredText,
    router,
    rxRowNotCoveredText,
    rxTableNotCoveredText,
    showNoResultsFound,
    showRxDrugCoverageTable,
    sitecoreContext,
    stage,
    state,
    topRow,
    topRowNoLink,
    year,
    zipcode,
    viewPlanDetailsLabel,
    viewApplyToEnrollLabel,
    pageEditing,
    useCavulus,
    crmPrefix,
    cx,
    resolvePlanClassName,
    isEnrollAllowedForPlan,
    router.query,
  ]);

  return {
    isLoading: stage !== LoadingState.end,
    showNoResultsFound,
    tables,
  };
}
